/**
 * SalesActivities.tsx — Sales / Activities feed with the Log Activity sheet
 * Design: Golden Stack — clean card list, type-coloured icons, modal for all activity types
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 * Off-scale text-12 labels stay arbitrary by the GATE 0-A map.
 */
import { useState, useRef, useEffect } from 'react'
import { Plus, MoreHorizontal, Check } from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesActivity } from '../../types'
import { useAsync, fetchActivities, fetchContacts, completeActivity, mapActivity } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
// Slice C1 (R1): the sheet and its create handler live in LogActivityHost.
import { MARK_DONE_FAILED } from './LogActivitySheet'
import { CONTACTS_CAP } from './LogActivityHost'
import { TYPE_CONFIG, useLogActivity } from './logActivityShared'

// ── Main page ─────────────────────────────────────────────────────────────────

export function SalesActivities() {
  const { data, loading, error } = useAsync(
    () => Promise.all([fetchActivities(), fetchContacts({ limit: CONTACTS_CAP })]),
    [],
  )
  const [activities, setActivities] = useState<SalesActivity[]>([])
  const [typeFilter, setTypeFilter] = useState<'all' | SalesActivity['type']>('all')
  const [showDone, setShowDone] = useState(true)
  const [marking, setMarking] = useState<Record<string, boolean>>({})

  // Seed local state from the fetched list (local state also holds optimistic adds).
  useEffect(() => { if (data) setActivities(data[0]) }, [data])

  // The host announces every activity that now EXISTS (the server's row,
  // pending if the complete that followed failed); the list inserts it as
  // slice B's page handler did. Opening goes through the host (R1).
  const logActivity = useLogActivity()
  const logButton = useRef<HTMLButtonElement>(null)
  useEffect(() => logActivity?.subscribe(created => setActivities(prev => [mapActivity(created), ...prev])), [logActivity])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const contacts = data?.[1] ?? []
  // The wire names a contact by id only; the name comes from the contacts
  // read the picker already needs. An id the page does not carry renders as
  // the RAW id with a note naming the bound — never a blank, which would read
  // the same as "no contact" (the §35 ruling-3 / OwnerSelect rule).
  const contactById = new Map(contacts.map(c => [c.id, c]))

  const filtered = activities.filter(a => {
    if (typeFilter !== 'all' && a.type !== typeFilter) return false
    if (!showDone && a.done) return false
    return true
  })

  const pending = activities.filter(a => !a.done)
  const done    = activities.filter(a => a.done)

  // Both writes go to the routes Deal detail already uses. The row on screen
  // is always the SERVER's row (mapActivity of the response), never a
  // client-made one, and a success toast fires only after a confirmed 2xx.
  async function handleMarkDone(id: string) {
    if (marking[id]) return
    setMarking(prev => ({ ...prev, [id]: true }))
    try {
      const updated = await completeActivity(id)
      setActivities(prev => prev.map(a => a.id === id ? mapActivity(updated) : a))
      toast.success('Activity marked as done')
    } catch (e) {
      toast.error(apiErrorMessage(e, MARK_DONE_FAILED))
    } finally {
      setMarking(prev => { const next = { ...prev }; delete next[id]; return next })
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Activities"
        subtitle={`${pending.length} pending · ${done.length} completed today`}
        action={
          <button ref={logButton} className="k-btn k-btn--lg inline-flex items-center gap-2"
            onClick={() => logActivity?.open({ contacts }, () => logButton.current)}>
            <Plus size={14} /> Log Activity
          </button>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'email', 'call', 'meeting', 'note', 'task'] as const).map(t => {
          const count = t === 'all' ? activities.length : activities.filter(a => a.type === t).length
          const cfg = t !== 'all' ? TYPE_CONFIG[t] : null
          return (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors flex items-center gap-2 ${typeFilter === t ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}
            >
              {cfg && <span style={{ color: cfg.color }}>{cfg.icon}</span>}
              <span className="capitalize">{t === 'all' ? 'All' : cfg!.label}</span>
              <span className={`text-overline font-mono px-2 py-1 rounded-full ${typeFilter === t ? 'bg-accent text-ink-on-accent' : 'bg-surface-panel text-ink-faint'}`}>{count}</span>
            </button>
          )
        })}
        <button
          onClick={() => setShowDone(!showDone)}
          className={`ml-auto px-3 py-2 rounded-full text-12 font-medium transition-colors border ${showDone ? 'bg-surface-control text-ink-secondary border-edge-control hover:border-edge-control-hover' : 'bg-ink-primary text-ink-on-accent border-ink-primary'}`}
        >
          {showDone ? 'Hide completed' : 'Show completed'}
        </button>
      </div>

      {/* Activity list */}
      <div className="k-card">
        <div className="divide-y divide-edge-row">
          {filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-13 text-ink-muted">No activities match your filter.</div>
          ) : (
            filtered.map(a => {
              const cfg = TYPE_CONFIG[a.type]
              const contact = a.contactId ? contactById.get(a.contactId) : undefined
              return (
                <div key={a.id} className={`flex items-start gap-4 px-5 py-4 hover:bg-surface-row-hover transition-colors ${a.done ? 'opacity-60' : ''}`}>
                  {/* Type icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-control flex items-center justify-center mt-1" style={{ background: cfg.bg, color: cfg.color }}>
                    {cfg.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-13 font-medium leading-snug ${a.done ? 'line-through text-ink-muted' : 'text-ink-primary'}`}>
                          {a.subject}
                        </p>
                        {a.description && (
                          <p className="text-12 text-ink-secondary mt-1 leading-snug">{a.description}</p>
                        )}
                        {/* Only what the row has. Three contact cases: no
                            contactId on the wire → no contact cell; resolved →
                            avatar + name (+ company when the record has one);
                            an id past the contacts read → the raw id and the
                            bound, no avatar (initials cut from a UUID would be
                            invented). The deal is always its raw id — the wire
                            has no title and the page holds no deals read. */}
                        {(a.contactId || a.deal) && (
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {contact && (
                              <>
                                <Avatar name={contact.name} size={18} />
                                <span className="text-12 text-ink-secondary">{contact.name}</span>
                                {contact.company && (
                                  <>
                                    <span className="text-ink-disabled">·</span>
                                    <span className="text-12 text-ink-faint">{contact.company}</span>
                                  </>
                                )}
                              </>
                            )}
                            {a.contactId && !contact && (
                              <span className="text-12 text-ink-secondary">{a.contactId} — not in the contacts read (first {CONTACTS_CAP} contacts)</span>
                            )}
                            {a.deal && (
                              <>
                                {a.contactId && <span className="text-ink-disabled">·</span>}
                                <span className="text-11 bg-surface-panel text-ink-secondary px-2 py-1 rounded-full">{a.deal}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-11 text-ink-faint">{a.time}</span>
                        <span className="text-11 text-ink-faint">by {a.owner}</span>
                        {!a.done && (
                          <button
                            onClick={() => handleMarkDone(a.id)}
                            disabled={Boolean(marking[a.id])}
                            className="w-6 h-6 rounded-full border-2 border-edge-control hover:border-accent hover:bg-surface-panel transition-colors flex items-center justify-center"
                            title="Mark as done"
                          >
                            <Check size={11} className="text-ink-faint" />
                          </button>
                        )}
                        {a.done && (
                          <div className="w-6 h-6 rounded-full bg-success-bg flex items-center justify-center">
                            <Check size={11} className="text-success" />
                          </div>
                        )}
                        <button onClick={() => toast.info('Activity options coming soon')} className="p-1 rounded hover:bg-surface-nav-hover transition-colors">
                          <MoreHorizontal size={14} className="text-ink-faint" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
